import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Chip,
    OutlinedInput,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import supplierService from '../../../services/supplierService';
import storeService from '../../../services/storeService';

const SupplierForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);

    // A supplier can now serve multiple stores. Only SUPER_ADMIN manages
    // which ones (a regular STORE_USER's supplier is always scoped to their
    // own store server-side, same restriction as everywhere else) — so the
    // multi-select only shows for SUPER_ADMIN, on both create and edit
    // (edit too, since adding an existing supplier to another store is
    // the whole point of this being a list now).
    // NOTE: `user.store` (an object) doesn't actually exist on UserDTO — the
    // real field is the flat `storeId`/`storeName` (see backend UserDTO) —
    // this mismatch predates this change (also present in authSlice.js) and
    // isn't fixed here to stay in scope; it just means we can't reliably
    // preselect "the admin's current store" below, so the multi-select
    // starts empty for SUPER_ADMIN on create.
    const isSuperAdmin = user?.globalRole === 'SUPER_ADMIN';

    const [formData, setFormData] = useState({
        storeIds: [],
        name: '',
        contactName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        isActive: true,
    });

    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isSuperAdmin) {
            fetchStores();
        }
        if (isEdit) {
            fetchSupplier();
        }
    }, [id, isSuperAdmin]);

    const fetchStores = async () => {
        try {
            const data = await storeService.getAllStores();
            setStores(data || []);
        } catch (err) {
            console.error('Failed to fetch stores:', err);
            toast.error(t('stores.fetchError'));
        }
    };

    const fetchSupplier = async () => {
        try {
            const data = await supplierService.getSupplierById(id);
            setFormData({
                storeIds: data.storeIds || [],
                name: data.name || '',
                contactName: data.contactName || '',
                email: data.email || '',
                phone: data.phone || '',
                address: data.address || '',
                city: data.city || '',
                country: data.country || '',
                isActive: data.isActive !== undefined ? data.isActive : true,
            });
        } catch (error) {
            toast.error(t('suppliers.fetchError'));
            console.error(error);
            navigate('/suppliers');
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('validation.supplierNameRequired');
        }

        if (isSuperAdmin && formData.storeIds.length === 0) {
            newErrors.storeIds = t('validation.storeRequired');
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('validation.invalidEmail');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error(t('common.saveError'));
            return;
        }

        setLoading(true);
        try {
            if (isEdit) {
                await supplierService.updateSupplier(id, formData);
                toast.success(t('suppliers.updateSuccess'));
            } else {
                await supplierService.createSupplier(formData);
                toast.success(t('suppliers.createSuccess'));
            }
            navigate('/suppliers');
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/suppliers')}>
                    {t('common.back')}
                </Button>
                <Typography variant="h4">
                    {isEdit ? t('suppliers.editSupplier') : t('suppliers.createSupplier')}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Card>
                    <CardContent>
                        <Grid container spacing={3}>
                            {/* Store Selector — SUPER_ADMIN only, multi-select: a supplier
                                can now serve several stores. Shown on both create and
                                edit, since adding an existing supplier to another store
                                is the whole point of this being a list. */}
                            {isSuperAdmin && (
                                <Grid item xs={12}>
                                    <FormControl
                                        fullWidth
                                        required
                                        error={Boolean(errors.storeIds)}
                                    >
                                        <InputLabel>{t('suppliers.stores')}</InputLabel>
                                        <Select
                                            multiple
                                            value={formData.storeIds}
                                            label={t('suppliers.stores')}
                                            onChange={(e) => handleChange('storeIds', e.target.value)}
                                            input={<OutlinedInput label={t('suppliers.stores')} />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((storeId) => {
                                                        const store = stores.find(s => s.id === storeId);
                                                        return (
                                                            <Chip key={storeId} label={store ? `${store.name} (${store.code})` : storeId} size="small" />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {stores.map(store => (
                                                <MenuItem key={store.id} value={store.id}>
                                                    <Checkbox checked={formData.storeIds.includes(store.id)} />
                                                    <ListItemText primary={`${store.name} (${store.code})`} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Typography variant="caption" color={errors.storeIds ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.5 }}>
                                            {errors.storeIds || t('suppliers.storesHelp')}
                                        </Typography>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Supplier Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('suppliers.name')}
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    error={Boolean(errors.name)}
                                    helperText={errors.name}
                                />
                            </Grid>

                            {/* Contact Person */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('suppliers.contactName')}
                                    value={formData.contactName}
                                    onChange={(e) => handleChange('contactName', e.target.value)}
                                />
                            </Grid>

                            {/* Email */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.email')}
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email}
                                />
                            </Grid>

                            {/* Phone */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('common.phone')}
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                            </Grid>

                            {/* Address */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('common.address')}
                                    multiline
                                    rows={2}
                                    value={formData.address}
                                    onChange={(e) => handleChange('address', e.target.value)}
                                />
                            </Grid>

                            {/* City */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('suppliers.city')}
                                    value={formData.city}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                />
                            </Grid>

                            {/* Country */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('suppliers.country')}
                                    value={formData.country}
                                    onChange={(e) => handleChange('country', e.target.value)}
                                />
                            </Grid>

                            {/* Active Status */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => handleChange('isActive', e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={t('common.active')}
                                />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                                    {t('common.inactive')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                    >
                        {loading ? t('common.save') + '...' : isEdit ? t('suppliers.editSupplier') : t('suppliers.createSupplier')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/suppliers')}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default SupplierForm;